// Each organization's waste-type basket (GET/POST/PATCH/DELETE/PUT
// /organization/:org/waste-types), typed as the market's OrganizationWasteType.

import type { OrganizationWasteType } from '../../services/organization.service';
import {
  DEMO_MATERIAL_IDS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  OUTLET_BASKET_MATERIAL_IDS,
  RECYCLER_BASKET_MATERIAL_IDS,
  categoryByCode,
  materialById,
} from '../core';
import { fail } from './errors';

interface BasketEntry {
  material_id: string;
  client_custom_id: string | null;
}

/** The recycler's own SKU codes for the streams it trades most. */
const RECYCLER_CUSTOM_IDS: Record<string, string> = {
  [DEMO_MATERIAL_IDS.occ]: 'RGR-OCC-11',
  [DEMO_MATERIAL_IDS.mixed_paper]: 'RGR-MXP-12',
  [DEMO_MATERIAL_IDS.office_paper]: 'RGR-SOP-13',
  [DEMO_MATERIAL_IDS.newspaper]: 'RGR-ONP-14',
  [DEMO_MATERIAL_IDS.pet]: 'RGR-PET-21',
  [DEMO_MATERIAL_IDS.hdpe]: 'RGR-HDP-22',
  [DEMO_MATERIAL_IDS.pp]: 'RGR-PPR-24',
  [DEMO_MATERIAL_IDS.aluminium_cans]: 'RGR-UBC-31',
  [DEMO_MATERIAL_IDS.steel_scrap]: 'RGR-HMS-32',
  [DEMO_MATERIAL_IDS.copper]: 'RGR-CU-33',
};

const OUTLET_CUSTOM_IDS: Record<string, string> = {
  [DEMO_MATERIAL_IDS.occ]: 'NKH-CRT',
  [DEMO_MATERIAL_IDS.pet]: 'NKH-PET',
  [DEMO_MATERIAL_IDS.uco]: 'NKH-UCO',
};

let baskets: Record<string, BasketEntry[]> = {};

export function resetBaskets(): void {
  baskets = {
    [DEMO_RECYCLER_ORG_ID]: RECYCLER_BASKET_MATERIAL_IDS.map((id) => ({
      material_id: id,
      client_custom_id: RECYCLER_CUSTOM_IDS[id] ?? null,
    })),
    [DEMO_OUTLET_ORG_ID]: OUTLET_BASKET_MATERIAL_IDS.map((id) => ({
      material_id: id,
      client_custom_id: OUTLET_CUSTOM_IDS[id] ?? null,
    })),
  };
}

function entriesOf(orgId: string): BasketEntry[] {
  return baskets[orgId] ?? [];
}

export function toWasteType(entry: BasketEntry): OrganizationWasteType {
  const material = materialById(entry.material_id);
  const category = material ? categoryByCode(material.category_code) : undefined;
  return {
    material_id: entry.material_id,
    material_type_id: material?.material_type_id ?? null,
    name: material?.name_en ?? entry.material_id,
    code: material?.code ?? null,
    parent_category: category?.name_en ?? null,
    category_code: material?.category_code ?? null,
    status: 'active',
    client_custom_id: entry.client_custom_id,
  };
}

/** The basket, sorted by name like the backend. */
export function listWasteTypes(orgId: string): OrganizationWasteType[] {
  return entriesOf(orgId)
    .map(toWasteType)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function basketMaterialIds(orgId: string): Set<string> {
  return new Set(entriesOf(orgId).map((e) => e.material_id));
}

/** Basket membership by catalog id or material_types alias id (equal in the demo). */
export function isInBasket(orgId: string, materialOrAliasId: string): boolean {
  return entriesOf(orgId).some(
    (e) => e.material_id === materialOrAliasId || materialById(e.material_id)?.material_type_id === materialOrAliasId,
  );
}

function cleanCustomId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed.slice(0, 64);
}

export function addWasteType(orgId: string, materialId: string, clientCustomId: unknown): OrganizationWasteType {
  const material = materialById(materialId);
  if (!material) fail(404, 'Unknown or inactive waste type');
  if (isInBasket(orgId, materialId)) {
    fail(409, `'${material.name_en}' is already one of this organization's waste types`);
  }
  const entry: BasketEntry = { material_id: materialId, client_custom_id: cleanCustomId(clientCustomId) };
  baskets = { ...baskets, [orgId]: [...entriesOf(orgId), entry] };
  return toWasteType(entry);
}

export function updateWasteType(orgId: string, materialId: string, clientCustomId: unknown): OrganizationWasteType {
  const current = entriesOf(orgId).find((e) => e.material_id === materialId);
  if (!current) fail(404, 'This waste type is not one of the organization\'s waste types');
  const next: BasketEntry = { ...current, client_custom_id: cleanCustomId(clientCustomId) };
  baskets = {
    ...baskets,
    [orgId]: entriesOf(orgId).map((e) => (e.material_id === materialId ? next : e)),
  };
  return toWasteType(next);
}

export function removeWasteType(orgId: string, materialId: string): void {
  if (!entriesOf(orgId).some((e) => e.material_id === materialId)) {
    fail(404, 'This waste type is not one of the organization\'s waste types');
  }
  baskets = { ...baskets, [orgId]: entriesOf(orgId).filter((e) => e.material_id !== materialId) };
}

/** Replace the whole selection, keeping the custom ids of entries that stay. */
export function replaceWasteTypes(orgId: string, materialIds: string[]): OrganizationWasteType[] {
  const unknown = materialIds.find((id) => !materialById(id));
  if (unknown) fail(404, 'Unknown or inactive waste type');
  const existing = new Map(entriesOf(orgId).map((e) => [e.material_id, e]));
  const unique = Array.from(new Set(materialIds));
  baskets = {
    ...baskets,
    [orgId]: unique.map((id) => existing.get(id) ?? { material_id: id, client_custom_id: null }),
  };
  return listWasteTypes(orgId);
}
